
import {React, useState, useContext, useEffect, useRef} from "react"
import {useStyle} from '../../context/styleContext/styleContext'
import ReactGA from 'react-ga4';
import { Paper } from '@mui/material';
import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";

export default function ReverbLearning(){

  const { style, setStyle } = useStyle();
  const [width, setWidth] = useState(window.innerWidth)

function handleWindowSizeChange() {
    setWidth(window.innerWidth);
}
useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
        window.removeEventListener('resize', handleWindowSizeChange);
    }
}, []);

const isMobile = width <= 768;

useEffect(() => {
  document.title = 'Parish Audio | Cutting-Edge Audio Processing | Reverb';
}, []);

ReactGA.send({
    hitType:"pageview",
    page:"learning/reverb",
  })

  const [height, setHeight] = useState(window.innerHeight)

  useEffect(() => {
    function handleResize() {      
      setWidth(window.innerWidth)
      setHeight(window.innerHeight)
  }
  
    window.addEventListener('resize', handleResize)
  })

const topContainer = useRef();
    useEffect(() => {
        // To make sure page starts from the top
        topContainer.current.scrollIntoView({ block: "end" });
    }, []);




    return(
        <>
        <div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

          <h1 className='h1-A' ref={topContainer}>Reverb</h1>
          <Divider className="mobile-card-divider" />
            <p className='p-A'>Reverb — short for reverberation — is one of the most fundamental and widely used effects in music, sound design, and acoustics. From cathedral choirs to guitar solos drenched in atmosphere, reverb has shaped the way we experience sound both naturally and artificially.</p>
          <h2 className='h2-A'>What Is Reverb?</h2>
            <p className='p-A'>Reverb is the persistence of sound after the original source has stopped. It occurs when sound waves reflect off surfaces like walls, ceilings, floors, and objects in a space, creating a dense, decaying tail of reflections that our ears perceive as ambiance.</p>
            <p className='p-A'>Unlike an echo — which you can hear as a distinct repetition of a sound — reverberation consists of many overlapping reflections that arrive within milliseconds of each other. The result is a smooth, continuous wash of sound that helps us determine the size, shape, and material of the environment we’re in.</p>
            <p className='p-A'>Reverb is everywhere: in nature (caves, forests), in architecture (cathedrals, theaters), and in recordings. It's a crucial part of how we interpret spatial information through hearing.</p>
          <h2 className='h2-A'>Why Reverb Matters</h2>
          <p className='p-A'>Reverb adds realism, dimension, and depth to sound. It gives recordings a sense of place. For music producers and mix engineers, reverb is a powerful creative tool. It can make a vocal feel intimate or distant, give drums punch or atmosphere, or make a guitar solo soar.</p>
          <p className='p-A'>But reverb is a double-edged sword. While it can enrich a track, too much reverb can make a mix sound muddy or obscure speech intelligibility — especially in dialogue-heavy content like podcasts or film. In audio engineering, there's a delicate balance between "wet" (reverberated) and "dry" (unaffected) sound.</p>
          <h2 className='h2-A'>Reverb Time (RT60) and Acoustics</h2>
          <p className='p-A'>The reverberation time, often abbreviated RT60, refers to the time it takes for reverb to decay by 60 decibels. It's one of the most important metrics in room acoustics.</p>
          <p className='p-A'>A cathedral might have an RT60 of 6–8 seconds, while a small recording booth might have an RT60 of less than 0.3 seconds. For speech clarity, rooms are often designed with shorter reverb times (0.5 to 1.0 seconds), while concert halls for orchestral music benefit from longer times (1.8 to 2.2 seconds).</p>
          <h3 className='h3-A'>Sabine’s Equation</h3>
          <p className='p-A'>The study of reverb began in the late 19th century with Wallace Clement Sabine, who developed the famous Sabine equation:</p>
          <p className='p-A'><strong>RT60 ≈ 0.161 × V / A</strong></p>
          <p className='p-A'>Where:</p>
          <ul className="a">
            <li>V = volume of the room (in m³)</li>
            <li>A = total absorption (in sabins)</li>
          </ul>
          <h3 className='h3-A'>Eyring’s Equation</h3>
          <p className='p-A'>For rooms with high absorption, Eyring's formula provides a more accurate model:</p>
          <p className='p-A'>RT60 ≈ -0.161 × V / (S × ln(1 - a))</p>
          <p className='p-A'>Where:</p>
          <ul className="a">
            <li>S = surface area of the room</li>
            <li>a = average absorption coefficient</li>
          </ul>
          <p className='p-A'>These equations are still widely used in architectural acoustics today.</p>
          <h2 className='h2-A'>Early Reflections and Perception</h2>
          <p>When a sound is produced in a space, the first reflections (arriving within ~50 milliseconds) are known as early reflections. They give us cues about the size and shape of the space. The later, denser cluster of reflections is the reverb tail.</p>         
          <p className='p-A'>Our brains are highly sensitive to this information. Even subtle changes in reverb can influence how we perceive the position, size, and importance of a sound source.</p>
          <h2 className='h2-A'>Types of Reverb</h2>
          <p className='p-A'>Reverb can be natural or artificially generated. Here are the main types used in audio production:</p>
          <ul className="a">
            <li className="li-A"><strong>Room Reverb - </strong>Simulates a small- to medium-sized space. Adds intimacy and realism. Great for pop, rock, and dialogue.</li>
            <li className="li-A"><strong>Hall Reverb - </strong>Mimics the acoustics of a large concert hall. Long decay and lush reflections. Ideal for orchestral music, pads, and cinematic scoring.</li>
            <li className="li-A"><strong>Chamber Reverb - </strong>Historically created by placing a speaker and mic in a reverberant room. Chamber reverb sounds thick and rich, but with more clarity than hall reverb.<br />Famous examples: Abbey Road Studios, Capitol Studios</li>
            <li className="li-A"><strong>Plate Reverb - </strong>
            
              Invented in the 1950s (e.g., EMT 140), plate reverb creates ambiance using vibrations in a sheet of metal. Known for its dense, smooth decay. Often used on vocals, snares, and guitars.<br /> A compact mechanical design using springs instead of metal plates. Common in guitar amps and Hammond organs. Produces a metallic, “boingy” character.
              <br />
              </li>
              
            <li className="li-A"><strong>Spring Reverb - </strong>
            A compact mechanical design using springs instead of metal plates. Common in guitar amps and Hammond organs. Produces a metallic, “boingy” character.
              <br />
              <br />

              - Famous in surf rock and dub reggae.
              <br />
              <br />

              - Units: Fender Twin Reverb, Demeter RV-1, RE-201 Space Echo

              <br/>
              <br/>
            <li className="li-A"><strong>Digital Reverb - </strong>Introduced in the 1970s with the EMT 250, digital reverb simulates acoustic environments using algorithms and delay lines. Extremely versatile.</li>
          </li>
          <li className="li-A"><strong>Convolution Reverb - </strong>Uses real-world impulse responses (IRs) to model the exact reverberation of a physical space. Often used in film and post-production for realism.</li>
          <li className="li-A"><strong>Shimmer Reverb - </strong>A modern, atmospheric type that adds pitch-shifting (usually an octave up or down) to the reverb tail. Perfect for ambient music and synth pads.</li>
          <li className="li-A"><strong>Gated Reverb - </strong>Made famous in the 1980s (Phil Collins' “In the Air Tonight”), gated reverb abruptly cuts off the reverb tail using a noise gate — creating a powerful, punchy effect on drums.</li>

          </ul>
          <h2 className='h2-A'>Reverb Parameters Explained</h2>
          <p className='p-A'>When using reverb, understanding key parameters is crucial:</p>
          <ul className="a">
          <li className="li-A"><strong>Pre-delay: </strong>Time between dry signal and onset of reverb. Adds separation.</li>
          <li className="li-A"><strong>Decay (RT60): </strong>Duration of the reverb tail.</li>
          <li className="li-A"><strong>Room Size: </strong>Affects the density and length of reflections.</li>
          <li className="li-A"><strong>Damping: </strong>Controls how high and low frequencies decay.</li>
          <li className="li-A"><strong>Dry/Wet Mix: </strong>Blend between unprocessed (dry) and reverberated (wet) signal.</li>
          <li className="li-A"><strong>EQ: </strong>Tailor reverb response using high-pass or low-pass filters to avoid muddiness.</li>


</ul>
<h2 className='h2-A'>Reverb in Music Production</h2>       
          <p className='p-A'>Most modern music is recorded “dry” in acoustically treated rooms. Reverb is then added during the mixing process using plugins or outboard gear.</p>
          <p className='p-A'>Producers use reverb to:</p>

          <ul className="a">
          <li className="li-A">Place vocals in a virtual space</li>
          <li className="li-A">Add realism to virtual instruments</li>
          <li className="li-A">Create transitions and effects</li>
          <li className="li-A">Make mixes sound cohesive</li>

</ul>
         <h2 className='h2-A'>Reverb in Film and Sound Design</h2>   
         <p className='p-A'>In film and game audio, reverb recreates physical spaces or enhances emotional cues. A gunshot in a parking garage has different reflections than one in a forest. Sound designers often use convolution reverb to match dialogue to on-screen environments.</p>
         <p className='p-A'>Foley artists also use reverb to add depth to footsteps, door creaks, and environmental sounds.</p>
         <p className='p-A'>Example: Hans Zimmer’s score for Interstellar uses long, celestial reverbs to reflect the vastness of space.</p>
         
         
         <h2 className='h2-A'>Notable Hardware</h2>         
          
          <p className='p-A'>Here are some legendary and modern tools to explore:</p>
          <h3 className='h3-A'>Classic Hardware Units:</h3>
          <ul className="a">
            <li className="li-A"><strong>EMT 140 - </strong>Plate reverb standard</li>
            <li className="li-A"><strong>Lexicon 224 - </strong>Digital hall reverb, heard on countless records</li>
            <li className="li-A"><strong>AMS RMX16 - </strong>80s digital staple (gated reverb)</li>
            <li className="li-A"><strong><a href='https://www.roland.com/global/products/rc_re-201_space_echo/' target="_blank" rel="noopener noreferrer">RE-201 Space Echo</a> - </strong>Tape echo with spring reverb</li>
            <li className="li-A"><strong><a href='https://www.fender.com/products/65-twin-reverb' target="_blank" rel="noopener noreferrer">Fender Twin Reverb</a> - </strong>Spring tank in an amp</li>

</ul>
<div style={{ width:'95%', maxWidth:'400px', textAlign:'left'}}>
      <WikiCommonsImage width="100%" bottomText="1965 Fender Twin Reverb Blonde" bottomTextLinkUrl="https://www.fender.com/products/65-twin-reverb" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Fender_Twin_Reverb_Blonde.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/d/d5/Fender_Twin_Reverb_Blonde.jpg" alt = "1965 Fender Twin Reverb Blonde"  title = "Paulus 2, CC BY-SA 4.0 <https://creativecommons.org/licenses/by-sa/4.0>, via Wikimedia Commons" />
  </div>

<h2 className='h2-A'>Reverb Tips for Beginners</h2>
<br />
<ul className="a">
            <li className="li-A"><strong>Add an EQ after reverb </strong>to tame harshness or muddiness.</li>
            <li className="li-A"><strong>High-pass your reverb </strong>to prevent low-end rumble.</li>
            <li className="li-A"><strong>Fine-tune wet/dry levels  </strong>to highlight certain words or instruments.</li>
            <li className="li-A"><strong>Less is more:  </strong>subtle reverb often sounds more natural.</li>

</ul>
<h2 className='h2-A'>Final Thoughts</h2> 
          <p className='p-A'>Reverb is more than just an effect — it’s an essential ingredient in how we experience sound. Whether you're crafting a lo-fi bedroom track, designing a soundscape for film, or mixing a live concert, knowing how to wield reverb will elevate your work to a professional level.</p>
          <p className='p-A'>From natural acoustics to plate reverbs and modern shimmer tails, the creative possibilities are endless. Explore, experiment, and let your ears guide you.</p>
          <br />
         <br />
         <br />
        <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
      </div>


        
        </>

    )
}